
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  Stack,
  Alert,
  Skeleton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  ReceiptLong as ReceiptLongIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  PointOfSale as PosIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useProtectedRoute } from "../hooks/useProtectedRoute";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

type StatColor = "primary" | "secondary" | "success" | "error" | "warning" | "info";

type SummaryData = {
  totalClients: number | string;
  totalTickets: number | string;
  totalInvoices: number | string;
  outstandingPayments: number | string; // may arrive as string -> coerce
  lowStockItems: number | string;
};

type RecentItem = {
  id?: string;
  refId?: string;
  type: "Ticket" | "Invoice" | "Payment" | "POS" | "Inventory" | "Client";
  ref: string;
  client: string;
  status: string;
  amount?: number | string;
  date: string; // ISO
};

type LowStockItem = {
  _id: string;
  name: string;
  sku?: string;
  quantity: number;
  reorderLevel: number;
};

type StatCard = {
  key: string;
  label: string;
  value: number;
  icon: JSX.Element;
  color: StatColor;
  help?: string;
  isCurrency?: boolean;
};

export default function Home() {
  useProtectedRoute();
  const { token } = useAuth();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const [lowStock, setLowStock] = useState<LowStockItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // --- helpers: robust number coercion & currency formatting ---
  const toNum = (v: unknown): number => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;
    // strip everything except digits, dot, minus (handles "$1,234.50")
    const n = Number(String(v).replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const fmtAmt = (v: unknown) =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(toNum(v));

  const fmtDate = (iso: string) =>
    new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(new Date(iso));

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, recentRes, lowRes] = await Promise.all([
        api.get("/dashboard/summary", { headers: { Authorization: `Bearer ${token}` } }),
        api.get("/activity/recent?limit=6", { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] as RecentItem[] })),
        api.get("/inventory/low-stock?limit=6", { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] as LowStockItem[] })),
      ]);

      setSummary(summaryRes.data as SummaryData);
      setRecent((recentRes.data as RecentItem[]) || []);
      setLowStock((lowRes.data as LowStockItem[]) || []);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (e?.response?.status === 401 ? "Not authenticated. Please login." : "Failed to load dashboard.")
      );
    } finally {
      setLoading(false);
    }
  };

  const activityLink = (r: RecentItem): string | null => {
    const id = r.refId || r.id || "";
    if (!id) return null;
    switch (r.type) {
      case "Ticket":
        return `/tickets/${id}`;
      case "Invoice":
        return `/invoices/${id}`;
      case "Payment":
      case "POS":
        return `/pos/sale/${id}`;
      case "Inventory":
        return r.ref?.startsWith?.("PO-") ? `/purchase-orders/${id}` : `/inventory/${id}`;
      case "Client":
        return `/clients/${id}/profile`;
      default:
        return null;
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Normalize stats to pure numbers (so UI always renders)
  const stats: StatCard[] = useMemo(
    () => [
      {
        key: "clients",
        label: "Clients",
        value: toNum(summary?.totalClients ?? 0),
        icon: <PersonIcon />,
        color: "primary",
        help: "Active clients",
      },
      {
        key: "tickets",
        label: "Tickets",
        value: toNum(summary?.totalTickets ?? 0),
        icon: <AssignmentIcon />,
        color: "secondary",
        help: "Job tickets",
      },
      {
        key: "invoices",
        label: "Invoices",
        value: toNum(summary?.totalInvoices ?? 0),
        icon: <ReceiptLongIcon />,
        color: "success",
        help: "All invoices",
      },
      {
        key: "unpaid",
        label: "Unpaid Amount",
        value: toNum(summary?.outstandingPayments ?? 0), // <- robust coercion
        icon: <WarningIcon />,
        color: "error",
        help: "Outstanding balance",
        isCurrency: true,
      },
      {
        key: "lowstock",
        label: "Low Stock Items",
        value: toNum(summary?.lowStockItems ?? 0),
        icon: <InventoryIcon />,
        color: "warning",
        help: "At/below reorder level",
      },
    ],
    [summary]
  );

  const StatSkeleton = () => (
    <Card sx={{ borderRadius: 3, p: 1, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={48} height={48} />
          <Box flex={1}>
            <Skeleton width="35%" />
            <Skeleton width="55%" />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 0 } }}>
      {/* Quick Actions */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === "light"
              ? `linear-gradient(135deg, ${theme.palette.primary.light}14, ${theme.palette.background.paper})`
              : undefined,
        }}
      >
        <CardContent sx={{ pb: 1.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
            <Typography variant="h6" fontWeight={800}>
              Quick Actions
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={loadData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Create, sell, and manage faster.
          </Typography>
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0, gap: 1.5, flexWrap: "wrap" }}>
          <Button
            fullWidth={isSmDown}
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to="/clients/add"
          >
            Add Client
          </Button>
          <Button
            fullWidth={isSmDown}
            variant="contained"
            color="secondary"
            startIcon={<AssignmentIcon />}
            component={Link}
            to="/tickets/create"
          >
            New Ticket
          </Button>
          <Button
            fullWidth={isSmDown}
            variant="contained"
            color="success"
            startIcon={<ReceiptLongIcon />}
            component={Link}
            to="/invoices/create"
          >
            Create Invoice
          </Button>
          <Button
            fullWidth={isSmDown}
            variant="contained"
            color="info"
            startIcon={<PointOfSaleIcon />}
            component={Link}
            to="/pos"
          >
            Open POS
          </Button>
          <Button
            fullWidth={isSmDown}
            variant="outlined"
            color="warning"
            startIcon={<InventoryIcon />}
            component={Link}
            to="/inventory"
          >
            View Inventory
          </Button>
        </CardActions>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {loading
          ? Array.from({ length: 5 }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${idx}`}>
                <StatSkeleton />
              </Grid>
            ))
          : stats.map((s) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={s.key}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all .2s ease",
                    "&:hover": { boxShadow: 4, transform: { md: "translateY(-2px)" } },
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none",
                      background: `linear-gradient(180deg, transparent 0%, ${theme.palette[s.color as StatColor].main}10 100%)`,
                    }}
                  />
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar
                        sx={{
                          bgcolor: theme.palette[s.color as StatColor].main,
                          width: 48,
                          height: 48,
                        }}
                      >
                        {s.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                          {s.isCurrency ? fmtAmt(s.value) : Intl.NumberFormat().format(s.value)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.label}
                        </Typography>
                      </Box>
                    </Stack>
                    {s.help && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                        {s.help}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
      </Grid>

      {/* Content: Recent Activity + Low Stock */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" fontWeight={800}>
                  Recent Activity
                </Typography>
                <Button size="small" variant="text" component={Link} to="/activity">
                  View all
                </Button>
              </Stack>
              <Divider sx={{ mb: 2 }} />

              {/* Table for md+; Card list for small screens */}
              {loading ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Table size="small" aria-label="recent-activity">
                    <TableBody>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={`row-skel-${i}`}>
                          <TableCell colSpan={5}>
                            <Skeleton height={30} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : recent.length === 0 ? (
                <Alert severity="info" icon={false}>
                  No recent activity yet. Create a ticket or invoice to get started 🚀
                </Alert>
              ) : isMdUp ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Table size="small" aria-label="recent-activity">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Ref / Client</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right">Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recent.map((r, i) => (
                        <TableRow
                          key={`recent-${i}`}
                          hover
                          onClick={() => { const link = activityLink(r); if (link) navigate(link); }}
                          sx={{ cursor: activityLink(r) ? 'pointer' : 'default' }}
                        >
                          <TableCell>
                            <Chip
                              size="small"
                              label={r.type}
                              color={r.type === "Invoice" ? "success" : r.type === "Payment" ? "info" : "secondary"}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {r.ref}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {r.client}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: r.status.toLowerCase() === "overdue" ? theme.palette.error.main : undefined,
                              }}
                            >
                              {r.status}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtAmt(r.amount)}</TableCell>
                          <TableCell align="right">{fmtDate(r.date)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                // Mobile-friendly list
                <Stack spacing={1.5}>
                  {recent.map((r, i) => (
                    <Paper
                      key={`recent-card-${i}`}
                      variant="outlined"
                      onClick={() => { const link = activityLink(r); if (link) navigate(link); }}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        gap: 1,
                        alignItems: "center",
                        cursor: activityLink(r) ? 'pointer' : 'default',
                      }}
                    >
                      <Chip
                        size="small"
                        label={r.type}
                        color={r.type === "Invoice" ? "success" : r.type === "Payment" ? "info" : "secondary"}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {r.ref}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {r.client}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: r.status.toLowerCase() === "overdue" ? theme.palette.error.main : "inherit",
                        }}
                      >
                        {r.status}
                      </Typography>
                      <Box gridColumn="1 / span 2" />
                      <Typography variant="body2">{fmtAmt(r.amount)}</Typography>
                      <Typography variant="caption" color="text.secondary" textAlign="right">
                        {fmtDate(r.date)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Low Stock */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent>
              <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                Low Stock
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={`low-skel-${i}`} height={28} />)
                  : lowStock.length === 0
                  ? (
                    <Alert severity="success" icon={false}>
                      Inventory looks healthy. 🎉
                    </Alert>
                  )
                  : lowStock.map((it) => {
                      const needs = Math.max(it.reorderLevel - it.quantity, 0);
                      return (
                        <Stack
                          key={it._id}
                          direction={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            border: `1px dashed ${theme.palette.divider}`,
                            gap: 0.5,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {it.name} {it.sku ? `· ${it.sku}` : ""}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {it.quantity} / Reorder: {it.reorderLevel}
                            </Typography>
                          </Box>
                          <Chip size="small" color="warning" label={needs > 0 ? `Need ${needs}` : "Reorder"} />
                        </Stack>
                      );
                    })}
              </Stack>
            </CardContent>
            <CardActions sx={{ p: 2 }}>
              <Button fullWidth variant="outlined" component={Link} to="/inventory">
                Manage Inventory
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// Local alias so we can use a semantic name above
function PointOfSaleIcon(props: Record<string, unknown>) {
  return <PosIcon {...props} />;
}
